import { useId, useState } from "react";
import { Link } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { Button, ErrorState, KebabMenu, lazyBlock, LoadingSkeleton, SideSheet, Tabs, tabId, useToast } from "../../ui";
import { useNextT } from "../../i18n";
import { reportErrorText } from "../../visits/errors";
import type { ApprovalRef } from "../api";
import { useInboxCounts, useUndoDoneAll, useUpdatesDoneAll } from "../api";
import type { InboxTab } from "../openInbox";
import { ReviewSlot } from "./ReviewSlot";
import s from "./NotificationsPanel.module.scss";

// The tabs' content is its own chunk (out of the main bundle): fetched when the panel first shows a tab.
const ToApproveTab = lazyBlock(() => import("./ToApproveTab").then((m) => ({ default: m.ToApproveTab })));
const UpdatesTab = lazyBlock(() => import("./UpdatesTab").then((m) => ({ default: m.UpdatesTab })));
const HandledTab = lazyBlock(() => import("./HandledTab").then((m) => ({ default: m.HandledTab })));

type Props = {
  open: boolean;
  onClose: () => void;
  /** What the bell (or a pop-up, or a banner) asked for; `seq` changes on every ask, so the tab is chosen again. */
  request?: { tab?: InboxTab; seq: number };
};

/**
 * The new frame's notifications (boards 35, 39; owner answers 7–8): "To approve" (only for approvers — the counts
 * carry `to_approve` only for them) and "Updates" (every other notification, old ones included). Rows open the
 * review (F10, ReviewSlot) over the panel; links to pages close it. Never today's panel, no "Older notifications".
 */
export const NotificationsPanel = ({ open, onClose, request }: Props) => {
  const { t } = useNextT();
  const toast = useToast();
  const uid = useId();
  const countsQuery = useInboxCounts({ enabled: open });
  const counts = countsQuery.data;
  const approver = counts?.to_approve !== undefined;
  // The tab the user picked in the panel, for the request it was picked in. A new request (its `seq`) starts from
  // what it asks for, worked out during render: the first mount already shows (and fetches) the right tab.
  const seq = request?.seq ?? 0;
  const [picked, setPicked] = useState<{ seq: number; tab: InboxTab } | null>(null);
  const chosen: InboxTab | null = picked?.seq === seq ? picked.tab : request?.tab ?? null;
  // Not chosen: "To approve" when something waits there, else "Updates".
  const wanted: InboxTab = chosen ?? ((counts?.to_approve ?? 0) > 0 ? "approve" : "updates");
  const tab: InboxTab = wanted === "approve" && approver ? "approve" : "updates";
  const [reviewing, setReviewing] = useState<ApprovalRef | null>(null);
  // The ⋯ menu's "Handled" is one small read-only view over the tabs (owner 2026-10-30), not a third tab.
  const [handled, setHandled] = useState(false);
  const doneAll = useUpdatesDoneAll();
  const undoAll = useUndoDoneAll();

  // N2.3: no question; a toast with Undo for 10 seconds.
  const markAll = () => {
    if (doneAll.isPending) return;
    doneAll.mutate(undefined, {
      onSuccess: (r) => toast(r.undo
        ? { message: t("approvals.panel.marked_all"), actionLabel: t("ui.undo"), durationMs: 10_000,
            onAction: () => undoAll.mutate(r.undo!, { onSuccess: () => toast({ message: t("approvals.panel.marked_all_undone") }), onError: (e) => toast({ message: reportErrorText(e, t), tone: "danger" }) }) }
        : { message: t("approvals.panel.marked_all") }),
      onError: (e) => toast({ message: reportErrorText(e, t), tone: "danger" }),
    });
  };
  const markAllButton = !handled && tab === "updates" && (counts?.updates ?? 0) > 0
    ? <Button variant="ghost" loading={doneAll.isPending} onClick={markAll}>{t("approvals.panel.mark_all")}</Button>
    : undefined;
  const headerAction = (
    <>
      {markAllButton}
      <KebabMenu label={t("approvals.panel.more")} items={[handled
        ? { label: t("approvals.panel.back_to_notifications"), onSelect: () => setHandled(false) }
        : { label: t("approvals.handled.title"), onSelect: () => setHandled(true) }]} />
    </>
  );

  const ids = { prefix: `${uid}-inbox`, panel: `${uid}-inbox-panel` };
  // Which tabs there are depends on the counts (approvers only have "To approve"): wait for them first.
  const content = !counts
    ? countsQuery.isError ? <ErrorState body={t("approvals.panel.load_failed")} onRetry={() => void countsQuery.refetch()} />
      : <LoadingSkeleton rows={3} label={t("approvals.panel.loading")} />
    : handled ? <HandledTab onLeave={onClose} approver={approver} onReview={setReviewing} />
    : tab === "approve" ? <ToApproveTab onReview={setReviewing} /> : <UpdatesTab onLeave={onClose} />;

  return (
    <>
      <SideSheet open={open} title={t("approvals.panel.title")} onClose={onClose} headerAction={headerAction}>
        {approver && !handled ? (
          <div className={s.panel}>
            <Tabs label={t("approvals.panel.tabs")} active={tab} onChange={(id) => setPicked({ seq, tab: id as InboxTab })} ids={ids} items={[
              { id: "approve", label: t("approvals.panel.to_approve"), count: counts?.to_approve },
              { id: "updates", label: t("approvals.panel.updates"), count: counts?.updates },
            ]} />
            <div role="tabpanel" id={ids.panel} aria-labelledby={tabId(ids.prefix, tab)} className={s.tabBody}>{content}</div>
          </div>
        ) : (
          <div className={s.tabBody}>
            {/* A visible way back from Handled, not only inside ⋯ (walkthrough 2026-10-29, issue 19). */}
            {handled && <div><Button variant="ghost" icon={<ArrowBackRoundedIcon className={s.flip} />} onClick={() => setHandled(false)}>{t("approvals.panel.back_to_notifications")}</Button></div>}
            {content}
          </div>
        )}
        <p className={s.footLink}><Link to="/settings#notices" onClick={onClose}>{t("approvals.panel.notice_choices")}</Link></p>
      </SideSheet>
      <ReviewSlot reviewRef={reviewing} onClose={() => setReviewing(null)} onLeave={() => { setReviewing(null); onClose(); }} />
    </>
  );
};
